import { useMemo } from 'react'
import { Copy, Download } from 'lucide-react'
import { type Column, DataTable, type TableState } from '@/components/blocks/data-table'
import { Button } from '@/components/ui/Button'
import { absoluteTime, fileSize, relativeTime } from '@/lib'
import type { ConfigFile } from '@/models'

/* Sort/search metadata for applyTableState. The display columns, with cell
 * renderers, are built inside ConfigFilesTable so the checksum copy button can
 * reach the onCopyChecksum handler. */
export const CONFIG_COLUMNS: Column<ConfigFile>[] = [
  { key: 'name', header: 'Name', sortable: true },
  { key: 'size', header: 'Size', sortable: true, value: (row) => row.size ?? 0 },
  { key: 'uploadedAt', header: 'Uploaded', sortable: true },
]

interface ConfigFilesTableProps {
  rows: readonly ConfigFile[]
  total: number
  state: TableState
  onStateChange: (patch: Partial<TableState>) => void
  onCopyChecksum: (file: ConfigFile) => void
  onDownload: (file: ConfigFile) => void
  loading?: boolean
}

export function ConfigFilesTable({
  rows,
  total,
  state,
  onStateChange,
  onCopyChecksum,
  onDownload,
  loading,
}: ConfigFilesTableProps) {
  const columns = useMemo<Column<ConfigFile>[]>(
    () => [
      {
        key: 'name',
        header: 'Name',
        width: 'minmax(220px,1fr)',
        sortable: true,
        cell: (row) => (
          <div className="min-w-0">
            <span className="text-fg block truncate text-sm">{row.name}</span>
            <span className="text-fg-muted flex items-center gap-1 text-xs">
              <span className="truncate font-mono">{row.checksum}</span>
              <button
                type="button"
                onClick={() => onCopyChecksum(row)}
                aria-label={`Copy ${row.name} checksum`}
                className="text-fg-subtle hover:text-fg focus-visible:outline-accent shrink-0 rounded p-0.5 transition-colors outline-none focus-visible:outline-2 focus-visible:outline-offset-1"
              >
                <Copy className="size-3" />
              </button>
            </span>
          </div>
        ),
      },
      {
        key: 'size',
        header: 'Size',
        width: '110px',
        align: 'end',
        sortable: true,
        value: (row) => row.size ?? 0,
        cell: (row) => (
          <span className="text-fg-muted text-sm tabular-nums">{fileSize(row.size)}</span>
        ),
      },
      {
        key: 'uploadedAt',
        header: 'Uploaded',
        width: '200px',
        sortable: true,
        cell: (row) => (
          <span className="text-fg-muted text-sm" title={absoluteTime(row.uploadedAt)}>
            {relativeTime(row.uploadedAt)}
          </span>
        ),
      },
    ],
    [onCopyChecksum],
  )

  return (
    <DataTable
      columns={columns}
      rows={rows}
      total={total}
      state={state}
      onStateChange={onStateChange}
      rowKey={(row) => row.id}
      loading={loading}
      minWidth={480}
      showPagination={false}
      rowActions={(row) => (
        <Button
          variant="ghost"
          size="icon"
          aria-label={`Download ${row.name}.json`}
          onClick={() => onDownload(row)}
        >
          <Download />
        </Button>
      )}
    />
  )
}
